/**
 * The game's main canvas: sized to its CSS box at the adaptive pixel ratio. Every view is
 * drawn into it with CSS-pixel coordinates on top of a plain `scale(ratio)` transform.
 */
const GAP_COLOR = '#06020f';

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Surface {
  readonly ctx: CanvasRenderingContext2D | null;
  /** CSS size. */
  width = 1;
  height = 1;
  /** Device pixels per CSS pixel. */
  scale = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d', { alpha: false });
  }

  /** Sizes the backing store; returns true if it changed (the canvas was reset). */
  resize(width: number, height: number, scale: number): boolean {
    const pixelWidth = Math.max(1, Math.round(width * scale));
    const pixelHeight = Math.max(1, Math.round(height * scale));
    this.width = width;
    this.height = height;
    this.scale = scale;
    if (this.canvas.width === pixelWidth && this.canvas.height === pixelHeight) return false;
    this.canvas.width = pixelWidth;
    this.canvas.height = pixelHeight;
    return true;
  }

  /** Frees the backing store (the screen is going away). */
  release(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
  }

  /** Starts a frame: resets the transform and paints the gaps between views. */
  begin(rects: readonly Box[], gap: number): CanvasRenderingContext2D | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    if (rects.length > 1) {
      ctx.fillStyle = GAP_COLOR;
      for (const r of rects) {
        if (r.y > 0) ctx.fillRect(r.x - gap, r.y - gap, r.w + gap, gap);
        if (r.x > 0) ctx.fillRect(r.x - gap, r.y, gap, r.h);
      }
    }
    return ctx;
  }
}
